import React, { useEffect } from "react";
import { Routes, Route } from 'react-router-dom';
import { useLocation } from "react-router-dom";
import { useTheme } from "./contexts/ThemeContext";

import HomePage from "./components/home_page/HomePage";
import Login from "./components/users/Login"
import Register from "./components/users/Register";
import EmailVerification from "./components/users/EmailVerification";
import Create from "./components/documentation/Create";
import MyTemplates from "./components/documentation/MyTemplates";
import DocumentationMainPage from "./components/documentation/DocumentationMainPage";
import Document from "./components/documentation/Document";
import EditDocument from "./components/documentation/EditDocument";
import Help from "./components/support/Help";
import SupportTicket from "./components/support/SupportTicket";
import Account from "./components/users/Account";
import DeleteAccount from "./components/users/DeleteAccount";
import Search from "./components/shared/Search";
import Walkthrough from "./components/walkthroughs/Walkthrough";
import CreateWalkthrough from "./components/walkthroughs/CreateWalkthrough";
import WalkthroughPage from "./components/walkthroughs/WalkthroughPage"
import EditWalkthrough from "./components/walkthroughs/EditWalkthrough";
import Reset from "./components/users/Reset";
import TeamWorkspaceMainPage from "./components/team_workspaces/TeamWorkspaceMainPage";
import CreateWorkspace from "./components/team_workspaces/CreateWorkspace";
import CreateWorkspaceDocument from "./components/team_workspaces/CreateWorkspaceDocument";
import SharedWorkspaceMainPage from "./components/team_workspaces/SharedWorkspaceMainPage";
import WorkspaceDocument from "./components/team_workspaces/WorkspaceDocument"
import EditWorkspaceDocument from "./components/team_workspaces/EditWorkspaceDocument";
import CreateSharedWorkspaceDocument from "./components/team_workspaces/CreateSharedWorkspaceDocument";
import WorkspaceSettings from "./components/team_workspaces/WorkspaceSettings";
import ImportWorkspaceDocument from "./components/team_workspaces/ImportWorkspaceDocument";
import ImportWorkspaceSpace from "./components/team_workspaces/ImportWorkspaceSpace";
import ImportDocument from "./components/documentation/ImportDocument";
import WriterPull from "./components/writer_sync/WriterPull";
import WorkspaceWriterPull from "./components/writer_sync/WorkspaceWriterPull";
import ImportSpaceDocument from "./components/documentation/ImportSpaceDocument";
import MyNotebook from "./components/my_notebook/MyNotebook";
import NotebookSettings from "./components/my_notebook/NotebookSettings";
import DocumentMarkupList from "./components/my_notebook/document_markup/DocumentMarkupList";
import DocumentMarkupUploader from "./components/my_notebook/document_markup/DocumentMarkupUploader";
import SimpleDocumentMarkupEditor from "./components/my_notebook/document_markup/SimpleDocumentMarkupEditor";
import AudioTranscription from "./components/audio_transcription/AudioTranscription";
import CreateDocumentFromTranscription from "./components/audio_transcription/CreateDocumentFromTranscription";
import CreateWorkspaceWalkthrough from "./components/team_workspaces/CreateWorkspaceWalkthrough";
import TeamWorkspaceWalkthrough from "./components/team_workspaces/TeamWorkspaceWalkthrough";
import EditTeamWorkspaceWalkthrough from "./components/team_workspaces/EditTeamWorkspaceWalkthrough";
import QuickLinksManager from "./components/shared/QuickLinksManager";
import ContentDuplicateAnalysis from "./components/documentation/ContentDuplicateAnalysis";
import WorkspaceContentDuplicateAnalysis from "./components/team_workspaces/WorkspaceContentDuplicateAnalysis";
import StorageManagement from "./components/users/StorageManagement";
import StoragePlans from "./components/users/StoragePlans";
import ReferralDashboard from "./components/users/ReferralDashboard";
import PrivacyPolicy from "./components/legal/PrivacyPolicy";
import AdvancedSearch from "./components/shared/AdvancedSearch";
import SharedDocumentView from "./components/shared/SharedDocumentView";
import PdfExportDownload from "./components/shared/PdfExportDownload";
import BluebirdGames from "./components/games/BluebirdGames";

// Route-scoped workspace theme override (BBD-81): pages inside a workspace set
// the override through WorkspaceSidebar / WorkspaceSettings; leaving workspace
// routes restores the personal theme.
const ThemeRouteSync = () => {
        const location = useLocation();
        const { clearWorkspaceTheme } = useTheme();
        useEffect(() => {
                if (!/^\/workspace\//.test(location.pathname)) {
                        clearWorkspaceTheme();
                }
        }, [location.pathname, clearWorkspaceTheme]);
        return null;
};

const Main = () => {
        return (
                <>
                        <ThemeRouteSync />
                        <QuickLinksManager />
                        <Routes>
                                <Route path='/' element={<HomePage />} />
                                <Route path='/login' element={<Login />} />
                                <Route path='/reset' element={<Reset />} />
                                <Route path='/register' element={<Register />} />
                                <Route path='/verify-email' element={<EmailVerification />} />
                                <Route path='/create' element={<Create />} />
                                <Route path='/templates' element={<MyTemplates />} />
                                <Route path='/account/:uid' element={<Account />} />
                                <Route path='/delete-account' element={<DeleteAccount />} />
                                <Route path='/account/:uid/storage' element={<StorageManagement />} />
                                <Route path='/account/:uid/plans' element={<StoragePlans />} />
                                <Route path='/account/:uid/referrals' element={<ReferralDashboard />} />
                                <Route path='/documentation' element={<DocumentationMainPage />} />
                                <Route path='/documentation/analyze-duplicates' element={<ContentDuplicateAnalysis />} />
                                <Route path='/shared/:token' element={<SharedDocumentView />} />
                                <Route path='/exports/pdf/:jobId' element={<PdfExportDownload />} />
                                <Route path='/documentation/:type/:name/:uid' element={<Document />} />
                                <Route path='/documentation/:type/:name/:uid/edit' element={<EditDocument />} />
                                <Route path='/help' element={<Help />} />
                                <Route path="/help/support-ticket" element={<SupportTicket />} />
                                <Route path="/documentation/search" element={<Search />} />
                                <Route path="/documentation/advanced-search" element={<AdvancedSearch />} />
                                <Route path="/workspace/:customDomain/search" element={<AdvancedSearch />} />
                                <Route path="/walkthroughs" element={<Walkthrough />} />
                                <Route path="/walkthroughs/create" element={<CreateWalkthrough />} />
                                <Route path="/walkthroughs/:tutorialName/:originalAuthorUid" element={<WalkthroughPage />} />
                                <Route path="/walkthroughs/:tutorialName/:originalAuthorUid/edit" element={<EditWalkthrough />} />
                                <Route path="/workspaces" element={<TeamWorkspaceMainPage />} />
                                <Route path="/workspaces/create" element={<CreateWorkspace />} />
                                <Route path="/workspace/create/document" element={<CreateWorkspaceDocument />} />
                                <Route path="/workspace/:customDomain" element={<SharedWorkspaceMainPage />} />
                                <Route path="/workspace/:customDomain/analyze-duplicates" element={<WorkspaceContentDuplicateAnalysis />} />
                                <Route path="/workspace/:customDomain/:type/:id" element={<WorkspaceDocument />} />
                                <Route path="/workspace/:customDomain/edit/:type/:id/:documentName" element={<EditWorkspaceDocument />} />
                                <Route path="/workspace/:customDomain/create/document" element={<CreateSharedWorkspaceDocument />} />
                                <Route path="/workspace/:customDomain/create/walkthrough" element={<CreateWorkspaceWalkthrough />} />
                                <Route path="/workspace/:customDomain/edit/walkthrough/:walkthroughId" element={<EditTeamWorkspaceWalkthrough />} />
                                <Route path="/workspace/:customDomain/walkthroughs/:walkthroughId" element={<TeamWorkspaceWalkthrough />} />
                                <Route path="/workspace/:customDomain/import/document" element={<ImportWorkspaceDocument />} />
                                <Route path="/workspace/:customDomain/import/space" element={<ImportWorkspaceSpace />} />
                                <Route path="/workspace/:customDomain/settings" element={<WorkspaceSettings />} />
                                <Route path="/import-document" element={<ImportDocument />} />
                                <Route path="/pull-from-writer" element={<WriterPull />} />
                                <Route path="/workspace/:customDomain/pull-from-writer" element={<WorkspaceWriterPull />} />
                                <Route path="/import-space" element={<ImportSpaceDocument />} />
                                <Route path="/my-notebook" element={<MyNotebook />} />
                                <Route path="/my-notebook/:notebookId" element={<MyNotebook />} />
                                <Route path="/my-notebook/settings" element={<NotebookSettings />} />
                                <Route path="/my-notebook/document-markup" element={<DocumentMarkupList />} />
                                <Route path="/my-notebook/document-markup/upload" element={<DocumentMarkupUploader />} />
                                <Route path="/my-notebook/document-markup/:documentId" element={<SimpleDocumentMarkupEditor />} />
                                <Route path="/audio-transcription" element={<AudioTranscription />} />
                                <Route path="/audio-transcription/create-document/:jobId" element={<CreateDocumentFromTranscription />} />
                                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                                <Route path="/bluebird-games" element={<BluebirdGames />} />
                        </Routes>
                </>
        );
}

export default Main;